import {
  addCatalog,
  matchCatalog,
  searchCatalogs,
  synctoERP,
  updateCatalog,
} from '@/adapters/master';
import { IDataModal } from '@/interfaces/data-modal';
import { ICatalog, ICatalogRes } from '@/interfaces/master';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Badge,
  Flex,
  Form,
  FormProps,
  Modal,
  notification,
  Radio,
  Select,
  Table,
  TableColumnsType,
} from 'antd';
import { uniqueId } from 'lodash';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';

const _modal: IDataModal<ICatalog> = {
  open: false,
};

const CatalogPage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<ICatalog[]>([]);
  const [action, setAction] = useState<'create' | 'update' | 'match'>('create');
  const [selected, setSelected] = useState<ICatalog[]>([]);
  const [modal, setModal] = useState<IDataModal<ICatalog>>({ ..._modal });
  const [form] = Form.useForm();

  const { data, refetch } = useQuery<ICatalogRes, Error>({
    queryKey: ['search-catalog-list'],
    queryFn: searchCatalogs,
    retry: 0,
  });

  const addCatalogMutation = useMutation({
    mutationFn: async (data: Partial<ICatalog>) =>
      action === 'create' ? await addCatalog(data) : await updateCatalog(data),
    mutationKey: ['add-catalog'],
    onSuccess: data => {
      if (data.status === 1) {
        notification.success({ message: 'Sync Catalog สำเร็จ' });
        refetch();
      } else {
        notification.error({ message: 'Sync Catalog ไม่สำเร็จ' });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const handleSyncSelectedCatalogs = async () => {
    for (const catalog of selectedRows) {
      await addCatalogMutation.mutateAsync(catalog);
    }
  };

  const matchBrandApi = useMutation({
    mutationFn: async (data: Partial<ICatalog>) =>
      action === (await matchCatalog(data)),
    mutationKey: ['match-catalog'],
    onSuccess: data => {
      setModal({ ..._modal });
      notification.success({ message: 'Match Catalog สำเร็จ' });
      refetch();
      form.resetFields();
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const handleUpdateCode = useCallback((item: ICatalog) => {
    setModal({
      open: true,
      item: item,
    });
  }, []);

  const columns = useMemo(
    () =>
      [
        {
          title: 'ลำดับ',
          dataIndex: '',
          width: 40,
          align: 'center' as const,
          className: 'text-bold',
          render: (_: any, __: ICatalog, i: number) => (
            <div className="font-weight-bold">{i + 1}</div>
          ),
        },
        {
          title: 'Catalog Code',
          dataIndex: 'CAT_CODE',
          hidden: action === 'match',
        },
        {
          title: 'Catalog Description',
          dataIndex: 'DESCRIPTION',
        },
        {
          title: 'BENTO ID',
          dataIndex: 'CATALOG_SING_ID',
        },
        {
          title: '',
          dataIndex: 'BRAND_SING_ID',
          align: 'center',
          width: 120,
          hidden: action !== 'match',
          render: (_, rec) => (
            <button
              type="button"
              className="btn btn-success"
              onClick={() => handleUpdateCode(rec)}
            >
              UPDATE
            </button>
          ),
        },
      ] as TableColumnsType<ICatalog>,
    [action, handleUpdateCode],
  );

  const insert = data?.data?.insert;
  const update = data?.data?.update;
  const match = data?.data?.match;

  useEffect(() => {
    const listMap = { create: insert, update, match };
    setSelected(
      listMap[action]?.map(c => ({ ...c, _key: uniqueId('c-') })) || [],
    );
    setSelectedRowKeys([]);
  }, [action, insert, update, match]);

  const rowSelection = {
    onChange: (_: React.Key[], selectedRows: ICatalog[]) => {
      setSelectedRowKeys(selectedRows.map(row => row._key));
      setSelectedRows(selectedRows);
    },
    selectedRowKeys,
    getCheckboxProps: (record: ICatalog) => ({
      disabled: action === 'match',
    }),
  };

  const [search, SetSearch] = useState('');
  const [searchText1, setSearchText1] = useState('');
  const [searchText2, setSearchText2] = useState('');

  const filteredData = useMemo(() => {
    return selected.filter(item => {
      if (action === 'match') {
        const brandName = (item.DESCRIPTION || '').toLowerCase();
        const searchValue = search.trim().toLowerCase();
        return !searchValue || brandName.includes(searchValue);
      } else {
        const catalogCode = (item.CAT_CODE || '').toUpperCase();

        // Convert search values to uppercase for case-insensitive comparison
        const fromValue = searchText1.trim().toUpperCase() || '';
        const toValue =
          searchText2.trim().toUpperCase() || 'ZZZZZZZZZZZZZZZZZZZZ';

        // If both fields are empty, show all
        if (!fromValue && toValue === 'ZZZZZZZZZZZZZZZZZZZZ') {
          return true;
        }

        // If only one field has a value, check if catalog code contains that value
        if (fromValue && !searchText2.trim()) {
          return catalogCode.includes(fromValue);
        }

        if (!fromValue && searchText2.trim()) {
          return catalogCode.includes(toValue);
        }

        // For true BETWEEN comparison with both values
        // Check if any part of the catalog code is between the values
        for (let i = 0; i < catalogCode.length; i++) {
          for (let j = i + 1; j <= catalogCode.length; j++) {
            const subCode = catalogCode.substring(i, j);
            if (subCode >= fromValue && subCode <= toValue) {
              return true;
            }
          }
        }

        return false;
      }
    });
  }, [selected, action, search, searchText1, searchText2]);

  const onFinish: FormProps<ICatalog>['onFinish'] = async values => {
    await matchBrandApi.mutateAsync({
      ...values,
      CATALOG_SING_ID: modal.item?.CATALOG_SING_ID,
    });
  };

  const actionTOERP = useMutation({
    mutationFn: async () => await synctoERP({ MASTER_TYPE: 'CATALOG' }),
    mutationKey: ['sync-to-erp'],
    onSuccess: data => {
      notification.success({ message: 'Sync Catalog สำเร็จ' });
      refetch();
    },
    onError: error => {
      console.log('error', error);
    },
  });
  const handleSyncTOERP = async () => {
    await actionTOERP.mutateAsync();
  };

  return (
    <div>
      <Flex justify="space-between" gap={2} className="mb-3">
        <Flex align="start" gap={12}>
          <Radio.Group
            value={action}
            onChange={e => {
              setAction(e.target.value);
              setSearchText1('');
              setSearchText2('');
            }}
            size="large"
          >
            <Radio.Button value="match">
              จับคู่ <Badge count={match?.length || 0} />
            </Radio.Button>
            <Radio.Button value="create">
              เพิ่ม <Badge count={insert?.length || 0} />
            </Radio.Button>
            <Radio.Button value="update">
              แก้ไข/ปรับปรุง <Badge count={update?.length || 0} />
            </Radio.Button>
          </Radio.Group>
          {action !== 'match' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>จาก</div>
              <Select
                style={{ minWidth: 150 }}
                value={searchText1}
                onChange={e => {
                  setSearchText1(e || '');
                }}
                size="large"
                showSearch
                allowClear
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={Array.from(
                  new Set(selected.map(item => item.CAT_CODE)),
                )
                  .sort()
                  .map(code => ({
                    label: code,
                    value: code,
                  }))}
              />
              <div>ถึง</div>
              <Select
                style={{ minWidth: 150 }}
                value={searchText2}
                onChange={e => {
                  setSearchText2(e || '');
                }}
                size="large"
                showSearch
                allowClear
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={Array.from(
                  new Set(selected.map(item => item.CAT_CODE)),
                )
                  .sort()
                  .map(code => ({
                    label: code,
                    value: code,
                  }))}
              />
            </div>
          )}

          {action === 'match' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Select
                style={{ minWidth: 300 }}
                value={search}
                onChange={e => {
                  SetSearch(e || '');
                }}
                size="large"
                showSearch
                allowClear
                placeholder="ค้นหา"
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={Array.from(
                  new Set(selected.map(item => item.DESCRIPTION)),
                )
                  .sort()
                  .map(code => ({
                    label: code,
                    value: code,
                  }))}
              />
            </div>
          )}
        </Flex>
        <Flex gap={12}>
          <button
            type="button"
            className="btn btn-info"
            style={{ minWidth: 100 }}
            onClick={() => handleSyncTOERP()}
          >
            {actionTOERP.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span className="sr-only">Loading...</span>
              </>
            ) : (
              '< ดึงข้อมูลมา ERP'
            )}
          </button>
          <button
            type="button"
            className="btn btn-success"
            style={{ minWidth: 100 }}
            disabled={!selectedRowKeys.length}
            onClick={() => handleSyncSelectedCatalogs()}
          >
            {addCatalogMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span className="sr-only">Loading...</span>
              </>
            ) : (
              'ส่งข้อมูลไป BENTO >'
            )}
          </button>
        </Flex>
      </Flex>
      <Table<ICatalog>
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        columns={columns}
        dataSource={filteredData}
        size="small"
        bordered
        rowKey={r => r._key}
        pagination={false}
        locale={{ emptyText: 'ไม่พบรายการ' }}
      />
      <Suspense>
        <Modal
          open={modal.open}
          width={350}
          onCancel={() => setModal({ ..._modal })}
          loading={matchBrandApi.isPending}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                onFinish(values);
              })
              .catch(() => {
                // validation failed
              });
          }}
        >
          <Form
            form={form}
            name="brand"
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item<ICatalog>
              label="CATALOG CODE"
              name="CAT_CODE"
              rules={[{ required: true }]}
            >
              <Select
                style={{ minWidth: 150 }}
                size="large"
                showSearch
                allowClear
                placeholder="ค้นหา"
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={[...(insert || [])].sort().map(brand => ({
                  label: `${brand.CAT_CODE} - ${brand.DESCRIPTION}`,
                  value: brand.CAT_CODE,
                }))}
              />
            </Form.Item>
          </Form>
        </Modal>
      </Suspense>
    </div>
  );
};

export default CatalogPage;
